/// <amd-module name="Repos/Views/PullRequest/Util/Viewed" />
import { IGitCommitChange } from "Repos/Changes/Contracts";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { GitPullRequest } from "Repos/Common/Contracts/Git";

export const enum ViewedStatus {
    /**
     * User has not viewed this file yet, or file has changed since it was viewed in a previous iteration.
     */
    Pending = 1,

    /**
     * User marked as viewed this file on this iteration, or on a previous one and it has not been touched since.
     */
    Viewed = 2
}

export interface ViewedState {
    hashes: { [hash: string]: ViewedStatus };
}

export function deserializeViewedState(serializedViewedState: string): ViewedState {
    return serializedViewedState ? JSON.parse(serializedViewedState) : { hashes: [] };
}

export function buildCheckmarksMap(changes: IGitCommitChange[], viewedState: ViewedState): { [path: string]: boolean } {
    const map: { [path: string]: boolean } = {};
    if (viewedState && viewedState.hashes) {
        for (const change of changes) {
            if (!change.item.isFolder) {
                const path = getPath(change)!;
                map[path] = viewedState.hashes[getViewedKey(change)] === ViewedStatus.Viewed;

                let parentPath = path;
                while (parentPath !== "/") {
                    parentPath = getParentName(parentPath) || "/";
                    map[parentPath] = map[parentPath] === undefined ? map[path] : map[parentPath] && map[path];
                }
            }
        }
    }

    return map;
}

export function calculateViewedCount(changes: IChange[], viewedState: ViewedState) {
    return changes.filter(change => !change.isFolder && viewedState.hashes[getIChangeViewedKey(change)] === ViewedStatus.Viewed).length;
}

export function getUpdateProperties(pullRequest: GitPullRequest, changes: IChange[], isViewed: boolean) {
    const hashes = changes.map(getIChangeViewedKey);

    return {
        repositoryId: pullRequest.repository.id,
        pullRequestId: pullRequest.pullRequestId,
        modifyHashes: hashes,
        modifyViewedStatus: isViewed ? ViewedStatus.Viewed : ViewedStatus.Pending
    };
}

function getParentName(path: string): string {
    return path.substring(0, path.lastIndexOf("/"));
}

// If we ever need to change the hash we save, we'll need to support another hash format.
// Initially, we're starting generated hashes by "1@..." so they can be discriminated in the future.
const hashVersionMarker = "1";

function getIChangeViewedKey(change: IChange) {
    return getViewedKey({ objectId: change.objectId!, item: { path: change.path, originalPath: change.sourcePath } } as IGitCommitChange);
}

function getViewedKey(change: IGitCommitChange) {
    return `${hashVersionMarker}@${getShortHash(change.objectId!)}@${getPath(change)}`;
}

function getShortHash(hash: string) {
    return hash ? hash.substring(0, 8) : "00000000";
}

function getPath(change: IGitCommitChange) {
    return change.item.path || change.item.originalPath;
}
